'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

export default function LegalTranslationCaseStudy() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(43,98,239,0.06), transparent, rgb(8,11,18))' }} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-8">
          <Link href="/case-studies" className="inline-flex items-center gap-2 text-cobalt text-sm font-medium mb-8 hover:text-cobalt-glow transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m15 18-6-6 6-6" />
            </svg>
            Back to Case Studies
          </Link>
          <span className="text-cobalt font-mono text-sm tracking-widest uppercase mb-4 block">Legal</span>
          <h1 className="font-display font-bold text-4xl lg:text-5xl text-silver-text leading-tight mb-6 max-w-4xl">
            Large-Scale Legal Translation & Document Engineering for a Global Law Firm
          </h1>
          <p className="text-lg text-silver-muted max-w-2xl leading-relaxed mb-10">
            1 million+ words of Chinese-to-English legal content delivered in 7 calendar days - with preserved formatting, Bates Numbering, and human-reviewed quality assurance.
          </p>
          <div className="flex flex-wrap gap-6">
            {[
              { value: '1M+', label: 'Words Translated' },
              { value: '7 Days', label: 'Turnaround' },
              { value: '100%', label: 'On-Time Delivery' },
              { value: '25%', label: 'Cost Reduction' },
            ]?.map((stat) => (
              <div key={stat?.label} className="glow-card rounded-xl px-6 py-4 text-center min-w-[120px]">
                <div className="text-xl font-bold text-cobalt-glow">{stat?.value}</div>
                <div className="text-xs text-silver-muted mt-1">{stat?.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Content */}
      <section className="py-16 lg:py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-12">

              {/* Challenge */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">01</span>
                  The Challenge
                </h2>
                <p className="text-silver-muted leading-relaxed mb-4">
                  A major legal matter required more than one million words of Chinese-to-English legal content converted from complex PDFs into accurate, editable Word files under an extreme deadline. Source materials included PDF email correspondence, contracts, and other legal documents.
                </p>
                <p className="text-silver-muted leading-relaxed">
                  The core challenges were the tight 7-day turnaround, complex PDF formatting that needed to be preserved, maintaining legal accuracy across a massive volume, and controlling costs without compromising quality.
                </p>
              </div>

              {/* Scope */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">02</span>
                  Project Scope
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { title: 'Source Materials', desc: 'PDF email correspondence, contracts, and legal documents in Chinese' },
                    { title: 'Target Deliverable', desc: 'Formatted, editable Word documents preserving original layouts and Bates Numbering' },
                    { title: 'Language Pair', desc: 'Chinese to English (legal register)' },
                    { title: 'Volume', desc: '1 million+ words across hundreds of documents' },
                  ]?.map((item) => (
                    <div key={item?.title} className="glow-card rounded-xl p-5">
                      <h3 className="font-semibold text-silver-text text-sm mb-1">{item?.title}</h3>
                      <p className="text-xs text-silver-muted">{item?.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Solution */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">03</span>
                  The Into23 Solution
                </h2>
                <div className="space-y-4">
                  {[
                    { step: '1', title: 'Document Conversion', desc: '10 document engineers converted complex PDFs to editable Word format using AI-powered OCR, preserving all formatting and Bates Numbering.' },
                    { step: '2', title: 'AI-Powered Draft Translation', desc: 'AI translation accelerated throughput across the full 1M+ word volume, enabling parallel processing across the document set.' },
                    { step: '3', title: 'Native Linguist Review', desc: '25 native-level Chinese-English linguists reviewed all AI-generated translations for legal accuracy, clarity, and terminology consistency.' },
                    { step: '4', title: 'Final Formatting & Delivery', desc: 'Document engineers performed final formatting checks to maintain original structure, numbering, and layout before delivery.' },
                  ]?.map((item) => (
                    <div key={item?.step} className="flex gap-4 p-5 glow-card rounded-xl">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-bold">{item?.step}</div>
                      <div>
                        <h3 className="font-semibold text-silver-text text-sm mb-1">{item?.title}</h3>
                        <p className="text-xs text-silver-muted leading-relaxed">{item?.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Key Techniques */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">04</span>
                  Key Techniques
                </h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    'Parallelized workflow across 10 document engineers + 25 linguists',
                    'AI-assisted translation for throughput acceleration',
                    'AI-powered OCR for complex PDF conversion',
                    'Human review for legal accuracy and clarity',
                    'Terminology management for consistent legal language',
                    'Duplicate content reuse - 25% reduction in billable word count',
                  ]?.map((tech) => (
                    <div key={tech} className="flex items-start gap-2 p-3 glow-card rounded-lg">
                      <span className="w-1.5 h-1.5 rounded-full bg-cobalt-glow flex-shrink-0 mt-1.5" />
                      <span className="text-xs text-silver-muted">{tech}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Results */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">05</span>
                  Results
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { metric: '1M+ words in 7 days', detail: 'Full volume delivered on the extreme deadline' },
                    { metric: '25% cost reduction', detail: 'Through duplicate content reuse and AI-assisted translation' },
                    { metric: '100% on-time delivery', detail: 'All documents delivered within the 7-day window' },
                    { metric: 'Formatting preserved', detail: 'Original layouts and Bates Numbering maintained throughout' },
                  ]?.map((r) => (
                    <div key={r?.metric} className="glow-card rounded-xl p-5 border-l-2 border-cobalt/40">
                      <div className="font-semibold text-cobalt-glow text-sm mb-1">{r?.metric}</div>
                      <div className="text-xs text-silver-muted">{r?.detail}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="glow-card rounded-xl p-6">
                <h3 className="font-semibold text-silver-text mb-4">Project Details</h3>
                <dl className="space-y-3 text-sm">
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Industry</dt><dd className="text-silver-text">Legal</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Client</dt><dd className="text-silver-text">Global Law Firm</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Language Pair</dt><dd className="text-silver-text">Chinese → English</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Volume</dt><dd className="text-silver-text">1 million+ words</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Turnaround</dt><dd className="text-silver-text">7 calendar days</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Team</dt><dd className="text-silver-text">10 engineers + 25 linguists</dd></div>
                </dl>
              </div>

              <div className="glow-card rounded-xl p-6">
                <h3 className="font-semibold text-silver-text mb-3">Key Capabilities Used</h3>
                <ul className="space-y-2 text-sm text-silver-muted">
                  {['AI-powered OCR', 'Document engineering', 'AI-assisted translation', 'Legal linguist review', 'Terminology management', 'Bates Numbering preservation', 'Parallel workflow management']?.map((cap) => (
                    <li key={cap} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-cobalt-glow flex-shrink-0" />
                      {cap}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="glow-card rounded-xl p-6">
                <h3 className="font-semibold text-silver-text mb-3">Need Legal Translation?</h3>
                <p className="text-sm text-silver-muted mb-4">Talk to our team about high-volume legal translation programs.</p>
                <Link href="/get-a-quote">
                  <span className="block text-center px-4 py-3 bg-cobalt hover:bg-cobalt-glow text-white text-sm font-semibold rounded-lg transition-all">
                    Get a Quote
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
